import { Injectable } from "@angular/core";
import { AuthService } from "./auth.service";
import { ApiService } from "./api.service";
import { SettingsService } from "./settings.service";
import { SubscriptionService } from "./subscription.service";
import { take, timeout, TimeoutError } from "rxjs";
import { DialogService } from "./dialog.service";
import { DbService } from "./db.service";
import { SynchronizationService } from "./synchronization.service";
import { ToastService } from "./toast.service";
import { APIResponse, Utils } from "fakturnia-shared";
import { MatDialog } from "@angular/material/dialog";
import { UpdateRequiredDialogComponent } from "shared/dialogs/update-required-dialog/update-required-dialog.component";
import { KsefTokenDialogComponent } from "shared/dialogs/ksef-token-dialog/ksef-token-dialog.component";
import { settings } from "environments/environment";
import { GuidedTourService } from "./guided-tour.service";
import { ConnectedAccountsService } from "./connected-accounts.service";

// Limit czasu pojedynczej próby pobrania ustawień (wykrywa zawieszony request).
const APP_INIT_TIMEOUT = 15000;
// Bazowy odstęp między ponowieniami; rośnie liniowo do limitu poniżej.
const APP_INIT_RETRY_DELAY = 5000;
// Górny limit odstępu między ponowieniami.
const APP_INIT_MAX_RETRY_DELAY = 30000;

@Injectable({
  providedIn: 'root'
})
export class AppInitService {

  constructor(
    private _authService: AuthService,
    private _apiService: ApiService,
    private _settingsService: SettingsService,
    private _subscriptionService: SubscriptionService,
    private _synchronizationService:SynchronizationService, 
    private _dbService:DbService, 
    private _dialogService:DialogService,
    private _toastService:ToastService,
    private _dialog: MatDialog,
    private _guidedTourService: GuidedTourService,
    private _connectedAccountsService: ConnectedAccountsService
  ) 
  {

  }

  initializeApp() {
    return new Promise<void>((resolve) => {

      if (!this._authService.isLoggedIn()) {
        resolve();
        return;
      }

      this._loadUserSettings(resolve, 0);
    })
  }

  /**
   * Pobiera ustawienia użytkownika — krok WYMAGANY do działania apki. Bootstrap
   * pozostaje zablokowany (użytkownik widzi splash z index.html), a my ponawiamy
   * pobranie BEZ KOŃCA aż do sukcesu. Promise rozwiązujemy dopiero wtedy, gdy
   * stan apki jest faktycznie zasilony (w `_applySettings`).
   */
  private _loadUserSettings(resolveBootstrap: () => void, attempt: number): void {
    this._apiService.getUserSettings().pipe(
      timeout(APP_INIT_TIMEOUT)
    ).subscribe({

      next: (response: APIResponse) => {
        // Mamy poprawną odpowiedź z serwera. Błąd jej ZASTOSOWANIA nie zniknie
        // po ponowieniu (te same dane), więc nie zapętlamy splasha — logujemy
        // i wpuszczamy usera. Ponawiamy tylko realne błędy POBRANIA (niżej).
        this._applySettings(response, resolveBootstrap)
          .catch((err) => {
            console.error('Inicjalizacja: błąd zastosowania ustawień (wpuszczam mimo to):', err);
            resolveBootstrap();
          });
      },

      error: (err) => this._retryUserSettings(resolveBootstrap, attempt, err)

    });
  }

  /**
   * Planuje kolejne (nieskończone) ponowienie inicjalizacji. Splash z index.html
   * pozostaje widoczny — usera nie wpuszczamy do niezainicjalizowanej apki.
   */
  private _retryUserSettings(resolveBootstrap: () => void, attempt: number, err: unknown): void {
    // Odstęp rośnie liniowo do APP_INIT_MAX_RETRY_DELAY.
    const delay = Math.min(APP_INIT_RETRY_DELAY * (attempt + 1), APP_INIT_MAX_RETRY_DELAY);

    if (err instanceof TimeoutError) {
      console.warn(`Inicjalizacja: timeout ustawień, ponawiam za ${delay / 1000}s (próba ${attempt + 1}).`);
    } else {
      console.error(`Inicjalizacja: błąd pobrania ustawień, ponawiam za ${delay / 1000}s (próba ${attempt + 1}):`, err);
    }

    setTimeout(() => this._loadUserSettings(resolveBootstrap, attempt + 1), delay);
  }

  /**
   * Zasila stan aplikacji odpowiedzią z ustawieniami i odblokowuje bootstrap.
   * Wywoływane zarówno przy starcie, jak i przy udanym ponowieniu w tle.
   */
  private async _applySettings(response: APIResponse, resolveBootstrap: () => void): Promise<void> {

    console.warn("Intialize platform");

    if(response.success == false) {
      resolveBootstrap();

      setTimeout(()=> {
        this._toastService.warning(response.message)
        this._authService.logout({muted: true})
      },3000)

      return
    }

    // Set user role
    localStorage.setItem('role',response.data.user.role);

    // Sprawdź wymaganą wersję aplikacji
    if (response.data.requiredVersions?.minWebVersion) {
      const currentVersion = settings.version.replace('dev', '');
      if (Utils.isVersionLower(currentVersion, response.data.requiredVersions.minWebVersion)) {
        this._dialog.open(UpdateRequiredDialogComponent, {
          disableClose: true,
          width: '500px',
          hasBackdrop: true,
          closeOnNavigation: false,
          data: {
            currentVersion,
            requiredVersion: response.data.requiredVersions.minWebVersion
          }
        });
        resolveBootstrap();
        return;
      }
    }

    // Update user settings
    await this._settingsService.updateUserSettings(response.data);

    // Zasil cache powiązanych kont (do submenu "Przełącz konto") — backend zwraca je w user settings.
    this._connectedAccountsService.setAccounts(response.data.connectedAccounts);

    // Ustaw subskrypcję jeśli jest dostępna
    if (response.data.subscription) {
      this._subscriptionService.setSubscription(response.data.subscription);
    } else {
      // Oznacz jako załadowane nawet bez subskrypcji
      this._subscriptionService.markAsLoaded();
    }

    // Set user database
    this._dbService.setDatabase(response.data.user.userId);
    // Synchronize app
    this._synchronizationService.synchronize('App initializator')

    if(response.data.user.hasCompletedRegistration == false ||
      typeof response.data.user.hasCompletedRegistration == 'undefined') {
          this._dialogService.showSetUpCompanyDialog()
          .pipe(take(1))
            .subscribe((result: any) => {
              if (result && result.success) {
                this._startOnboarding();
              }
            })
      } else if (!response.data.user.hasSeenOnboarding) {
        this._startOnboarding();
      }
    resolveBootstrap();
  }

  /**
   * Uruchamia onboarding — guided tour na desktopie, dialog na mobile
   */
  private _startOnboarding(): void {
    // Subskrybuj żądanie połączenia z KSeF z guided tour
    this._guidedTourService.connectKsef$.pipe(take(1)).subscribe(() => {
      this._dialog.open(KsefTokenDialogComponent, {
        maxWidth: '1100px',
        width: '95vw',
        maxHeight: '90vh'
      });
    });

    // Daj chwilę na załadowanie layoutu
    setTimeout(() => {
      this._guidedTourService.start();
    }, 500);
  }

}
